import { colors, mappedColors } from '@pixee/design-system'
import { useId } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import * as styles from './spinner.css.ts'

export const SpinnerSizeSchema = z.enum(['16', '20', '24', '40', '64', '128', 'auto'])
export type SpinnerSize = z.infer<typeof SpinnerSizeSchema>
export const SpinnerVariantSchema = z.enum(['default', 'cool', 'warm', 'updating', 'unthemed'])
export type SpinnerVariant = z.infer<typeof SpinnerVariantSchema>

type SpinnerProps = {
  label?: string
  size?: SpinnerSize
  variant?: SpinnerVariant
}

// SVG <linearGradient> requires raw color values — CSS linear-gradient() strings
// (like GRADIENTS.veil/heat) can't be used as SVG fill/stroke. These stops mirror
// the GRADIENTS tokens from @pixee/design-system using the same underlying `colors`
// (static :root CSS vars) so they stay in sync with the design system palette.
const coolGradientStops = [
  { offset: '0%', stopColor: colors.secondary.wave },
  { offset: '35%', stopColor: colors.primary.drift },
  { offset: '70%', stopColor: colors.primary.ion },
  { offset: '100%', stopColor: colors.primary.nebula },
]

const warmGradientStops = [
  { offset: '0%', stopColor: colors.primary.pulse },
  { offset: '50%', stopColor: colors.primary.flare },
  { offset: '100%', stopColor: colors.primary.nova },
]

const unthemedTrack = `color-mix(in srgb, ${colors.neutral.halo} 20%, transparent)`

export function Spinner({ label = 'Action pending', size = '24', variant = 'default' }: SpinnerProps) {
  const gradientId = useId()

  const sizeMap = {
    '16': 16,
    '20': 20,
    '24': 24,
    '40': 40,
    '64': 64,
    '128': 128,
    // Approximate ratio so that the spinner matches the text height for button size small
    // TODO: find a better way for svg to scale to match the text height or container height
    auto: '1.4em',
  }

  const svgSize = sizeMap[size]

  const strokeWidthMap = {
    '16': 2,
    '20': 2,
    '24': 2,
    '40': 4,
    '64': 4,
    '128': 8,
    auto: 2,
  }
  const strokeWidth = strokeWidthMap[size]

  const isGradientStyle = variant === 'cool' || variant === 'warm'
  const gradientStops = variant === 'warm' ? warmGradientStops : coolGradientStops

  const finalArcStroke = match(variant)
    .with('cool', 'warm', () => `url(#${gradientId})`)
    .with('updating', () => mappedColors.spinner.updatingArc)
    .with('unthemed', () => colors.neutral.halo)
    .with('default', () => mappedColors.spinner.arc)
    .exhaustive()

  const trackStroke = match(variant)
    .with('unthemed', () => unthemedTrack)
    .otherwise(() => mappedColors.spinner.track)

  return (
    <span role="status" aria-label={label} aria-live="polite" className={styles.span}>
      <svg height={svgSize} width={svgSize} viewBox="0 0 16 16" fill="none" className={styles.spinner}>
        {isGradientStyle && (
          <defs>
            <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="8" x2="16" y2="8">
              {gradientStops.map(stop => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.stopColor} />
              ))}
            </linearGradient>
          </defs>
        )}
        <circle cx="8" cy="8" r="7" stroke={trackStroke} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
        <path
          d="M15 8a7.002 7.002 0 00-7-7"
          stroke={finalArcStroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </span>
  )
}
